import { useState, useEffect, useCallback } from 'react';
import { Mail, Play, Pause, Clock, CheckCircle, Send, TestTube } from 'lucide-react';
import api from '../utils/api';

export default function DripsTab({ headers }) {
  const [sequences, setSequences] = useState([]);
  const [queue, setQueue] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testSeq, setTestSeq] = useState('welcome');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q, l] = await Promise.all([
        api.get('/admin/drips', { headers }),
        api.get('/admin/drips/queue', { headers }),
        api.get('/admin/drips/log', { headers }),
      ]);
      setSequences(s.data);
      setQueue(q.data);
      setLog(l.data);
    } catch (e) {
      console.error('Drips fetch error:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSequence = async (id, enabled) => {
    try {
      await api.put(`/admin/drips/${id}/toggle`, { enabled }, { headers });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    try {
      await api.post('/admin/drips/enqueue-test', { email: testEmail, sequence_id: testSeq }, { headers });
      setTestEmail('');
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading drip sequences...</p>;

  return (
    <div className="space-y-8" data-testid="drips-tab">
      {/* Sequences */}
      <div>
        <h3 className="font-serif text-lg mb-4">Email Drip Sequences</h3>
        <div className="space-y-3">
          {sequences.map(seq => (
            <div key={seq.id} className={`bg-white/5 border rounded-sm p-5 ${seq.enabled ? 'border-green-500/20' : 'border-white/10'}`} data-testid={`drip-${seq.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-primary" />
                    <p className="font-serif text-sm">{seq.name}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${seq.enabled ? 'text-green-400 bg-green-500/10' : 'text-muted-foreground bg-white/5'}`}>
                      {seq.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{seq.description}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Trigger: <span className="text-foreground">{seq.trigger}</span> &middot; {seq.emails.length} emails &middot; {seq.total_sent} sent
                    {seq.total_sent > 0 && (
                      <> &middot; <span className="text-green-400">{seq.open_rate}% opens</span> &middot; <span className="text-blue-400">{seq.click_rate}% clicks</span></>
                    )}
                  </p>
                  {/* Email steps */}
                  <div className="mt-3 space-y-1">
                    {seq.emails.map((em, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>Step {em.step}: {em.delay_hours === 0 ? 'Immediately' : `After ${em.delay_hours}h`}</span>
                        <span className="text-foreground">{em.subject}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="shrink-0">
                  <button
                    onClick={() => toggleSequence(seq.id, !seq.enabled)}
                    className={`flex items-center gap-1 px-3 py-2 text-xs rounded-sm border ${seq.enabled ? 'text-amber-400 border-amber-500/30 hover:bg-amber-500/10' : 'text-green-400 border-green-500/30 hover:bg-green-500/10'}`}
                    data-testid={`toggle-drip-${seq.id}`}
                  >
                    {seq.enabled ? <><Pause className="w-3 h-3" /> Disable</> : <><Play className="w-3 h-3" /> Enable</>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test Drip */}
      <div className="bg-white/5 border border-white/10 rounded-sm p-5">
        <h3 className="font-serif text-lg mb-3 flex items-center gap-2"><TestTube className="w-4 h-4 text-primary" /> Test Drip</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={testSeq} onChange={e => setTestSeq(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm text-foreground" data-testid="test-drip-select">
            {sequences.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@example.com" type="email"
            className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 flex-1" data-testid="test-drip-email" />
          <button onClick={sendTest} className="bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold rounded-sm hover:bg-white hover:text-black transition-colors" data-testid="test-drip-send">
            <Send className="w-3 h-3 inline mr-1" /> Send Test
          </button>
        </div>
      </div>

      {/* Queue */}
      <div>
        <h3 className="font-serif text-lg mb-3">Pending Queue <span className="text-muted-foreground text-xs font-sans">({queue.length})</span></h3>
        {queue.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending emails in queue.</p>
        ) : (
          <div className="space-y-2">
            {queue.map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-3 flex items-center justify-between text-xs">
                <div>
                  <p className="text-foreground">{item.subject}</p>
                  <p className="text-muted-foreground">{item.email} &middot; Step {item.step}</p>
                </div>
                <div className="text-muted-foreground">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {new Date(item.send_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Log */}
      <div>
        <h3 className="font-serif text-lg mb-3">Recent Sends <span className="text-muted-foreground text-xs font-sans">({log.length})</span></h3>
        {log.length === 0 ? (
          <p className="text-sm text-muted-foreground">No emails sent yet. Enable a sequence to start sending.</p>
        ) : (
          <div className="space-y-2">
            {log.slice(0, 20).map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-green-400" />
                  <div>
                    <p className="text-foreground">{item.subject}</p>
                    <p className="text-muted-foreground">{item.email}</p>
                  </div>
                </div>
                <span className="text-muted-foreground">{new Date(item.sent_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
